/**
 * Availability Transformer
 *
 * Handles bidirectional transformation between:
 * - API response format (multiple variations for backward compatibility)
 * - Form state format (UI-friendly structure)
 * - Event payload format (for emitting to parent components)
 */
import { dayMapper, DAY_KEYS } from './day-mapper';
import { pricingService } from '../services/pricing.service';
// =============================================================================
// HELPER FUNCTIONS
// =============================================================================
/**
 * Extract a numeric field with multiple possible field names (backward compatibility)
 */
function extractNumber(source, ...fieldNames) {
    for (const field of fieldNames) {
        const value = source[field];
        if (typeof value === 'number')
            return value;
        if (typeof value === 'string') {
            const parsed = parseFloat(value);
            if (!isNaN(parsed))
                return parsed;
        }
    }
    return 0;
}
/**
 * Extract a nullable numeric field
 */
function extractNullableNumber(source, ...fieldNames) {
    for (const field of fieldNames) {
        const value = source[field];
        if (value === null)
            continue;
        if (typeof value === 'number')
            return value;
        if (typeof value === 'string') {
            const parsed = parseFloat(value);
            if (!isNaN(parsed))
                return parsed;
        }
    }
    return null;
}
/**
 * Extract a string field
 * Handles both string values and Time value objects with toString() method
 */
function extractString(source, ...fieldNames) {
    for (const field of fieldNames) {
        const value = source[field];
        if (typeof value === 'string' && value)
            return value;
        // Handle Time value objects that have a toString() method
        if (value && typeof value === 'object' && typeof value.toString === 'function') {
            const str = value.toString();
            if (str && str !== '[object Object]')
                return str;
        }
    }
    return '';
}
/**
 * Convert day range (startingDate to endingDate) to array of day labels
 */
function dayRangeToLabels(startDay, endDay) {
    if (!startDay || !endDay)
        return [];
    // Same day
    if (startDay === endDay) {
        const label = dayMapper.keyToLabel(startDay);
        return label ? [label] : [];
    }
    // Range of days
    const startIdx = DAY_KEYS.indexOf(startDay);
    const endIdx = DAY_KEYS.indexOf(endDay);
    if (startIdx === -1 || endIdx === -1)
        return [];
    const labels = [];
    for (let i = startIdx; i <= endIdx; i++) {
        const label = dayMapper.keyToLabel(DAY_KEYS[i]);
        if (label)
            labels.push(label);
    }
    return labels;
}
/**
 * Extract common pricing/capacity fields from a source object
 */
function extractCommonFields(source) {
    return {
        standardMaxParticipants: extractNumber(source, 'standardMaxParticipants', 'standard_max_participants', 'maxParticipants', 'max_participants') || 1,
        overcapacityMaxParticipants: extractNullableNumber(source, 'overcapacityMaxParticipants', 'overcapacity_max_participants'),
        overflowPrice: extractNullableNumber(source, 'overflowPrice', 'overflow_price'),
        netPrice: extractNumber(source, 'net_price', 'netPrice', 'basePrice', 'base_price'),
        priceType: (extractString(source, 'price_type', 'priceType') || 'PERSON'),
        commissionAmount: extractNumber(source, 'commission_amount', 'commissionAmount'),
        commissionType: (extractString(source, 'commission_type', 'commissionType') || 'PERCENTAGE'),
        taxRate: extractNumber(source, 'tax_rate', 'taxRate') || 0.13,
        allowReservations: source.allow_reservations !== false && source.allowReservations !== false,
    };
}
function calculateTotalPrice(formState) {
    return pricingService.round(pricingService.calculateTotalPrice({
        netPrice: Number(formState.netPrice) || 0,
        commissionAmount: Number(formState.commissionAmount) || 0,
        commissionType: formState.commissionType || 'PERCENTAGE',
        taxRate: formState.taxRate,
    }));
}
// =============================================================================
// MAIN TRANSFORMER
// =============================================================================
export const availabilityTransformer = {
    /**
     * Transform API availability data to form state
     */
    fromApiToForm(availability) {
        const result = {
            formState: {},
            selectedOption: 'all',
            editingId: null,
            showOverbookingConfig: false,
        };
        // Extract editing ID
        result.editingId = (availability.id ||
            availability.availability_id ||
            availability.override_id ||
            null);
        const type = availability.type;
        if (type === 'availability') {
            result.selectedOption = 'all';
            // Check for nested schedule array (from service drawer)
            const schedule = availability.schedule;
            if (schedule && Array.isArray(schedule) && schedule.length > 0) {
                const scheduleItem = schedule[0];
                // Extract days from schedule
                const daysOfWeek = scheduleItem.days_of_week;
                if (daysOfWeek && Array.isArray(daysOfWeek)) {
                    result.formState.days = dayMapper.keysToLabels(daysOfWeek);
                }
                // Extract times
                result.formState.startTime = extractString(scheduleItem, 'start_time', 'startTime');
                result.formState.endTime = extractString(scheduleItem, 'end_time', 'endTime');
                // Extract common fields from schedule
                Object.assign(result.formState, extractCommonFields(scheduleItem));
            }
            else {
                // Flat structure from calendar API
                const startDay = extractString(availability, 'startingDate');
                const endDay = extractString(availability, 'endingDate');
                result.formState.days = dayRangeToLabels(startDay, endDay);
                // Check both old (startingSchedule) and new (start_time) field names for backward compatibility
                result.formState.startTime = extractString(availability, 'startingSchedule', 'start_time');
                result.formState.endTime = extractString(availability, 'endingSchedule', 'end_time');
                // Extract common fields from availability
                Object.assign(result.formState, extractCommonFields(availability));
            }
        }
        else if (type === 'override') {
            result.selectedOption = 'range';
            result.formState.dateRange = {
                start: extractString(availability, 'startingDate', 'overrideStartingFrom'),
                end: extractString(availability, 'endingDate', 'overrideUntil'),
            };
            // Check both old (startingSchedule) and new (start_time) field names for backward compatibility
            result.formState.startTime = extractString(availability, 'startingSchedule', 'start_time');
            result.formState.endTime = extractString(availability, 'endingSchedule', 'end_time');
            // Extract common fields
            Object.assign(result.formState, extractCommonFields(availability));
        }
        // Determine if overbooking config should be shown
        result.showOverbookingConfig =
            result.formState.overcapacityMaxParticipants !== null ||
                result.formState.overflowPrice !== null;
        return result;
    },
    /**
     * Build create-calendar event payload
     */
    toCreateCalendarPayload(input) {
        const { formState, serviceId } = input;
        const selectedDays = dayMapper.labelsToKeys(formState.days);
        const totalPrice = calculateTotalPrice(formState);
        const data = {
            service_id: serviceId,
            schedule: [
                {
                    days_of_week: selectedDays,
                    start_time: formState.startTime,
                    end_time: formState.endTime,
                    standard_max_participants: formState.standardMaxParticipants,
                    overcapacity_max_participants: formState.overcapacityMaxParticipants,
                    overflow_price: formState.overflowPrice,
                    net_price: formState.netPrice,
                    total_price: totalPrice,
                    commission_amount: formState.commissionAmount,
                    commission_type: formState.commissionType,
                    price_type: formState.priceType,
                    tax_rate: formState.taxRate,
                    allow_reservations: formState.allowReservations,
                },
            ],
        };
        return { serviceId, data };
    },
    /**
     * Build create-override event payload
     */
    toCreateOverridePayload(input) {
        const { formState, serviceId } = input;
        const totalPrice = calculateTotalPrice(formState);
        const data = {
            overrideStartingFrom: formState.dateRange.start,
            overrideUntil: formState.dateRange.end,
            start_time: formState.startTime,
            end_time: formState.endTime,
            standard_max_participants: formState.standardMaxParticipants,
            overcapacity_max_participants: formState.overcapacityMaxParticipants,
            overflow_price: formState.overflowPrice,
            net_price: formState.netPrice,
            total_price: totalPrice,
            commission_amount: formState.commissionAmount,
            commission_type: formState.commissionType,
            price_type: formState.priceType,
            tax_rate: formState.taxRate,
            allow_reservations: formState.allowReservations,
        };
        return { serviceId, data };
    },
    /**
     * Build update-calendar event payload
     */
    toUpdateCalendarPayload(input) {
        const { formState, serviceId, editingId } = input;
        const selectedDays = dayMapper.labelsToKeys(formState.days);
        const totalPrice = calculateTotalPrice(formState);
        const data = {
            days_of_week: selectedDays,
            start_time: formState.startTime,
            end_time: formState.endTime,
            standard_max_participants: formState.standardMaxParticipants,
            overcapacity_max_participants: formState.overcapacityMaxParticipants,
            overflow_price: formState.overflowPrice,
            net_price: formState.netPrice,
            total_price: totalPrice,
            commission_amount: formState.commissionAmount,
            commission_type: formState.commissionType,
            price_type: formState.priceType,
            tax_rate: formState.taxRate,
            allow_reservations: formState.allowReservations,
        };
        return {
            serviceId,
            availabilityId: editingId ?? 0,
            data,
        };
    },
    /**
     * Build update-override event payload
     */
    toUpdateOverridePayload(input) {
        const { formState, serviceId, editingId } = input;
        const totalPrice = calculateTotalPrice(formState);
        const data = {
            date: formState.dateRange.start, // Primary date for single-day overrides
            overrideStartingFrom: formState.dateRange.start,
            overrideUntil: formState.dateRange.end,
            start_time: formState.startTime,
            end_time: formState.endTime,
            standard_max_participants: formState.standardMaxParticipants,
            overcapacity_max_participants: formState.overcapacityMaxParticipants,
            overflow_price: formState.overflowPrice,
            net_price: formState.netPrice,
            total_price: totalPrice,
            commission_amount: formState.commissionAmount,
            commission_type: formState.commissionType,
            price_type: formState.priceType,
            tax_rate: formState.taxRate,
            allow_reservations: formState.allowReservations,
        };
        return {
            serviceId,
            overrideId: editingId ?? 0,
            data,
        };
    },
};
